import { sendTokenToExtension } from "@/helpers/send-to-extension.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  useEmployeeChangePassword,
  useEmployeeMeInvalidate,
} from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { Button, useToasts, Input, useModals, Modal, ModalProps, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { z } from "zod";
import { ChangePasswordSource } from "@/types/employees/employee.types.ts";

export function ChangePasswordModal({ ...props }: ModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { setToken } = useAuthStore();
  const { showInfo } = useToasts();

  const ChangePasswordSchemaObject = z.object({
    password: z
      .string({ error: t("errors:password_required") })
      .min(1, t("errors:password_required"))
      .min(8, t("api_errors:minimum_value_must", { minimum: 8 }))
      .regex(/[a-z]/, t("api_errors:password_must_contain_lowercase"))
      .regex(/[A-Z]/, t("api_errors:password_must_contain_uppercase"))
      .regex(/[0-9]/, t("api_errors:password_must_contain_number"))
      .regex(/[!@#$%^&*(),.?":{}|<>]/, t("api_errors:password_must_contain_special")),
  });

  type ChangePasswordSchema = z.infer<typeof ChangePasswordSchemaObject>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ChangePasswordSchema>({
    mode: "onSubmit",
    resolver: zodResolver(ChangePasswordSchemaObject),
  });
  const employeeMeInvalidate = useEmployeeMeInvalidate();
  const changePasswordMutation = useEmployeeChangePassword(control);

  const onSubmit = handleSubmit(async (loginData) => {
    try {
      const data = await changePasswordMutation.mutateAsync({
        password: loginData.password,
        source: ChangePasswordSource.changePassword,
      });
      setToken(data.access_token);
      sendTokenToExtension(data.access_token);
      employeeMeInvalidate();
      showInfo({ title: t("core:password_updated") });
      analyticsService.trackEvent(analyticEvents.passwordChanged);
      close(id);
    } catch {
      // Errors are handled by the mutation hook
    }
  });

  return (
    <>
      <Modal
        {...props}
        title={t("common:change_password")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={changePasswordMutation.isPending}
              onClick={onSubmit}
            >
              {t("common:change")}
            </Button>
          </Stack>
        }
      >
        <form className="space-y-4 md:space-y-6" onSubmit={onSubmit}>
          <Input
            label={t("common:password")}
            type="password"
            {...register("password")}
            placeholder="••••••••"
            error={errors.password?.message}
          />
        </form>
      </Modal>
    </>
  );
}
